import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, PageHeader, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Panel, Section, StateView, formatBytes, formatRelative, useAuth, useToast } from "@ozpanel/ui";
import { ManagedBinaries, SchemaMigrate } from "./UpdateActions";
import { ComponentUpdates, OsUpdates } from "./UpdatePanels";

// Cluster binary updates.
//
// The drift was always measurable — /cluster/updates has reported `consistent:false` all along —
// and nothing surfaced it, so node10 ran one build while node11 ran another from four days
// earlier and every page on both looked healthy. A number nobody is shown is a number nobody
// acts on.

interface StagedInfo {
  path?: string;
  present?: boolean;
  sha256?: string;
  size_bytes?: number;
  staged_at?: string;
  signature_valid?: boolean;
}
interface NodeVersion {
  node: string;
  version: string;
}
interface Updates {
  self_version: string;
  staged_binary?: string;
  staged?: StagedInfo;
  nodes: NodeVersion[] | null;
  distinct_versions: number;
  consistent: boolean;
}
interface RolloutResult {
  node: string;
  ok: boolean;
  from?: string;
  to?: string;
  skipped?: string;
  detail?: string;
}

export function UpdatesScreen() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [confirm, setConfirm] = React.useState(false);
  const [results, setResults] = React.useState<RolloutResult[] | null>(null);

  const q = useQuery({
    queryKey: ["cluster-updates"],
    queryFn: () => client.apiFetch<Updates>("/cluster/updates"),
    refetchInterval: 30000,
  });

  const rollout = useMutation({
    mutationFn: () =>
      client.apiFetch<{ updated: number; attempted: number; results: RolloutResult[]; note?: string }>(
        "/cluster/updates/rollout",
        { method: "POST", body: JSON.stringify({}) },
      ),
    onSuccess: (r) => {
      setResults(r.results ?? []);
      notify({
        kind: r.updated === r.attempted ? "success" : "error",
        title: `${r.updated} of ${r.attempted} member(s) updated`,
        body: r.updated === r.attempted ? undefined : "The rollout stopped at the first failure — see the results below.",
      });
      void qc.invalidateQueries({ queryKey: ["cluster-updates"] });
    },
    onError: (e) => notify({ kind: "error", title: "Rollout failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const d = q.data;
  const staged = d?.staged;
  // A staged binary is only rollable if it is SIGNED. Offering the button without saying so
  // means every member refuses it one round trip at a time and the operator learns why last.
  const canRollout = Boolean(staged?.present && staged?.signature_valid);
  const self = d?.self_version;

  const nodeCols: DataListColumn<NodeVersion>[] = [
    { key: "node", label: "Node", sortable: true },
    {
      key: "version",
      label: "Running",
      sortable: true,
      render: (n) => (
        <span className="flex items-center gap-2">
          <code className="font-mono text-[12.5px]">{n.version || "unknown"}</code>
          {n.version === self ? (
            <StatusPill tone="ok">matches this node</StatusPill>
          ) : (
            <StatusPill tone="warn">differs</StatusPill>
          )}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Updates"
        subtitle="What each node is running, and rolling a signed release out to the members"
        actions={
          <Button variant={canRollout ? "accent" : "ghost"} disabled={!canRollout || rollout.isPending} onClick={() => setConfirm(true)}>
            {rollout.isPending ? "Rolling out…" : "Roll out to members"}
          </Button>
        }
      />
      <StateView loading={q.isLoading} error={q.error} errorContext="cluster versions">
        {d && (
          <>
            <Section title="Version across the cluster" hint={d.consistent ? "consistent" : `${d.distinct_versions} versions`}>
              {!d.consistent && (
                <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
                  <b>The nodes are not running the same build.</b> Each one is healthy on its own — that is what makes
                  this easy to miss. A schema migration applied by the newer binary is what turns it into an outage.
                </Panel>
              )}
              <DataList
                items={d.nodes ?? []}
                columns={nodeCols}
                getRowKey={(n) => n.node}
                emptyMessage="No nodes registered."
              />
            </Section>

            <Section title="Staged release" className="mt-6">
              {!staged?.present ? (
                <p className="text-[12.5px] text-muted">
                  Nothing staged on this node. Build a release and stage it here before rolling out.
                </p>
              ) : (
                <>
                  {/* Present and SIGNED are two facts. An unsigned staged binary is refused by
                      every member — including this one — so showing only "present" would make
                      the rollout button look available when it cannot work. */}
                  <div className="mb-3 flex flex-wrap gap-1.5">
                    <StatusPill tone="ok">staged</StatusPill>
                    <StatusPill tone={staged.signature_valid ? "ok" : "bad"}>
                      {staged.signature_valid ? "signature valid" : "NOT signed — every member will refuse it"}
                    </StatusPill>
                  </div>
                  <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12.5px]">
                    <dt className="text-muted">Staged</dt>
                    <dd>{staged.staged_at ? formatRelative(staged.staged_at) : "—"}</dd>
                    <dt className="text-muted">Size</dt>
                    <dd>{formatBytes(staged.size_bytes)}</dd>
                    <dt className="text-muted">SHA-256</dt>
                    <dd className="break-all font-mono text-[11.5px]">{staged.sha256}</dd>
                  </dl>
                  {!staged.signature_valid && (
                    <Panel className="mt-3 border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] p-3 text-[12.5px]">
                      Sign it first: <code className="font-mono">POST /cluster/updates/sign</code> on the node holding
                      the cluster CA key. Only that node can, and every node verifies with the CA certificate alone.
                    </Panel>
                  )}
                </>
              )}
            </Section>

            {results && (
              <Section title="Last rollout" className="mt-6" bodyClassName="p-0">
                <div className="p-4">
                  <DataList
                    items={results}
                    columns={[
                      { key: "node", label: "Node", sortable: true },
                      {
                        key: "ok",
                        label: "Result",
                        value: (r) => (r.ok ? "updated" : r.skipped || "failed"),
                        render: (r) =>
                          r.ok ? (
                            <StatusPill tone="ok">updated</StatusPill>
                          ) : r.skipped ? (
                            // Skipped is not failed. A node that could not be ASKED is unknown,
                            // and calling that a failure sends people to fix the wrong thing.
                            <StatusPill tone="warn">{r.skipped}</StatusPill>
                          ) : (
                            <StatusPill tone="bad">failed</StatusPill>
                          ),
                      },
                      { key: "to", label: "Now running", value: (r) => r.to || "", render: (r) => (r.to ? <code className="font-mono text-[12px]">{r.to}</code> : "—") },
                      { key: "detail", label: "Detail", value: (r) => r.detail || "", render: (r) => <span className="text-[11.5px] text-muted">{r.detail}</span> },
                    ]}
                    getRowKey={(r) => r.node}
                    emptyMessage=""
                  />
                </div>
              </Section>
            )}
          </>
        )}
      </StateView>

      <SchemaMigrate />
      <ManagedBinaries />
      <ComponentUpdates />
      <OsUpdates />

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Roll this release out to the member nodes?"
        destructive
        confirmLabel="Roll out"
        pending={rollout.isPending}
        onConfirm={() => {
          setConfirm(false);
          rollout.mutate();
        }}
        body={
          <div className="space-y-2">
            <p>
              Each member is updated <b>one at a time</b> and restarts its agent. The rollout stops at the first
              failure, so the remaining nodes keep running the version that works.
            </p>
            <p>
              <b>This node is not included.</b> It is driving the rollout — updating it would restart the process
              running the loop. Update it afterwards, once the members are healthy.
            </p>
          </div>
        }
      />
    </div>
  );
}
